import type { FoundationSubType, FoundationSuperType } from "building-types/foundations/foundationEnums";
import {
  FoundationSubSystemType,
  FoundationSuperSystemType,
  FOUNDATION_TYPES,
  FoundationCondition,
  StemWallType,
} from "building-types/foundations/foundationEnums";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import {
  type BuildingGenerationIssue,
  BuildingGenerationIssueType,
} from "building-types/settings/buildingGenerationIssue";
import type { FoundationHash, FoundationSystemSettings } from "building-types/settings/foundationSettingsTypes";
import { snakeCaseToTitleCase } from "common/utils/strings";

export function foundationHash<S extends FoundationSuperType, T extends FoundationSubType<S>>(
  superType: S,
  subType: T,
): FoundationHash<S, T> {
  return `${superType}.${subType}`;
}

export function foundationSubSystemTypes(
  foundationSuperSystemType: FoundationSuperSystemType | undefined,
): FoundationSubSystemType[] {
  return (
    (foundationSuperSystemType !== undefined ? FOUNDATION_TYPES[foundationSuperSystemType]?.slice() : undefined) ?? []
  ); // we need to clone this since it is a readonly array
}

export interface FoundationSystemTypeCombination {
  foundationSuperSystemType: FoundationSuperSystemType;
  foundationSubSystemType: FoundationSubSystemType;
}

export type FoundationSystemTypeCombinations = Record<
  FoundationHash<FoundationSuperSystemType, FoundationSubSystemType>,
  FoundationSystemTypeCombination
>;

/**
 * A table of all valid foundation system type combinations, keyed by foundation hash.
 * Excludes ISOLATED sub-system type as it's not valid for edge assemblies.
 */
export const getFoundationSystemTypeCombinations: () => FoundationSystemTypeCombinations = (() => {
  const combinations: Partial<FoundationSystemTypeCombinations> = {};

  for (const superType of Object.values(FoundationSuperSystemType)) {
    for (const subType of foundationSubSystemTypes(superType)) {
      if (subType === FoundationSubSystemType.ISOLATED) {
        continue;
      }
      const hash = foundationHash(superType, subType);
      combinations[hash] = {
        foundationSuperSystemType: superType,
        foundationSubSystemType: subType,
      };
    }
  }

  return () => combinations as FoundationSystemTypeCombinations;
})();

export function determineFoundationCondition(
  leftRoomClass: RoomClass,
  leftRoomDisabledFoundationGeneration: boolean,
  rightRoomClass: RoomClass,
  rightRoomDisabledFoundationGeneration: boolean,
  hasBrickledge: boolean = false,
  issues: BuildingGenerationIssue[] = [],
): FoundationCondition {
  // for now, patios generate X_EXTERIOR foundation conditions.
  // If they need their own FoundationCondition, this should be updated
  if (leftRoomClass === RoomClass.PATIO) {
    leftRoomClass = RoomClass.EXTERIOR;
  }
  if (rightRoomClass === RoomClass.PATIO) {
    rightRoomClass = RoomClass.EXTERIOR;
  }

  let minRoomClass = leftRoomClass;
  let maxRoomClass = rightRoomClass;
  const isDisabled = rightRoomDisabledFoundationGeneration || leftRoomDisabledFoundationGeneration;

  if (leftRoomClass > rightRoomClass) {
    const t = leftRoomClass;
    minRoomClass = rightRoomClass;
    maxRoomClass = t;
  }

  let condition: FoundationCondition;
  let partialCondition = RoomClass[minRoomClass] + "_";
  if (isDisabled) {
    partialCondition += "DISABLED";
  } else {
    if (hasBrickledge) {
      partialCondition += "BRICKLEDGE_";
    }
    partialCondition += RoomClass[maxRoomClass];
  }

  if (!FoundationCondition[partialCondition]) {
    // default back to condition without brick because we don't handle all foundation combinations
    condition = FoundationCondition[RoomClass[minRoomClass] + "_" + RoomClass[maxRoomClass]];
    if (!condition) {
      // in the event that this is really broken, this will void the whole foundation at that edge
      condition = FoundationCondition.EXTERIOR_EXTERIOR;
    }
    issues.push({
      type: BuildingGenerationIssueType.INVALID_FOUNDATION_CONDITION,
      invalidCondition: partialCondition,
      fallbackCondition: condition,
    });
  } else {
    condition = FoundationCondition[partialCondition];
  }

  return condition;
}

export const roomClassToPerimeterCondition: {
  [K in RoomClass]: FoundationCondition;
} = {
  [RoomClass.HOUSE]: FoundationCondition.HOUSE_EXTERIOR,
  [RoomClass.BASEMENT]: FoundationCondition.BASEMENT_EXTERIOR,
  [RoomClass.GARAGE]: FoundationCondition.GARAGE_EXTERIOR,
  [RoomClass.PORCH]: FoundationCondition.PORCH_EXTERIOR,
  [RoomClass.DECK]: FoundationCondition.DECK_EXTERIOR,
  [RoomClass.PATIO]: FoundationCondition.EXTERIOR_EXTERIOR,
  [RoomClass.EXTERIOR]: FoundationCondition.EXTERIOR_EXTERIOR,
};

export const isPerimeterFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.HOUSE_EXTERIOR]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.BASEMENT_EXTERIOR]: true,
  [FoundationCondition.BASEMENT_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.GARAGE_EXTERIOR]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.GARAGE_DOOR_EXTERIOR]: true,
  [FoundationCondition.PORCH_EXTERIOR]: true,
  [FoundationCondition.PORCH_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.DECK_EXTERIOR]: true,
};

export const isInteriorFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.HOUSE_HOUSE]: true,
  [FoundationCondition.HOUSE_GARAGE]: true,
  [FoundationCondition.HOUSE_PORCH]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_PORCH]: true,
  [FoundationCondition.HOUSE_DECK]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_DECK]: true,
  [FoundationCondition.HOUSE_EXTERIOR]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.HOUSE_DISABLED]: true,
};

export const isGarageFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.GARAGE_GARAGE]: true,
  [FoundationCondition.GARAGE_PORCH]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_PORCH]: true,
  [FoundationCondition.GARAGE_DECK]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_DECK]: true,
  [FoundationCondition.GARAGE_EXTERIOR]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.GARAGE_DOOR_EXTERIOR]: true,
  [FoundationCondition.GARAGE_DISABLED]: true,
};

export const isPorchFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.PORCH_PORCH]: true,
  [FoundationCondition.PORCH_DECK]: true,
  [FoundationCondition.PORCH_BRICKLEDGE_DECK]: true,
  [FoundationCondition.PORCH_EXTERIOR]: true,
  [FoundationCondition.PORCH_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.PORCH_DISABLED]: true,
};

export const isDeckFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.DECK_DECK]: true,
  [FoundationCondition.DECK_EXTERIOR]: true,
  [FoundationCondition.DECK_DISABLED]: true,
};

export const isExteriorFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.EXTERIOR_EXTERIOR]: true,
};

export const isBasementFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.BASEMENT_BASEMENT]: true,
  [FoundationCondition.BASEMENT_EXTERIOR]: true,
  [FoundationCondition.BASEMENT_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.BASEMENT_DISABLED]: true,
};

export const isSpanFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.HOUSE_HOUSE]: true,
  [FoundationCondition.BASEMENT_BASEMENT]: true,
  [FoundationCondition.GARAGE_GARAGE]: true,
  [FoundationCondition.PORCH_PORCH]: true,
  [FoundationCondition.DECK_DECK]: true,
};

export const isTransitionFoundationCondition: {
  [K in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.HOUSE_GARAGE]: true,
  [FoundationCondition.HOUSE_PORCH]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_PORCH]: true,
  [FoundationCondition.HOUSE_DECK]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_DECK]: true,
  [FoundationCondition.GARAGE_PORCH]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_PORCH]: true,
  [FoundationCondition.GARAGE_DECK]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_DECK]: true,
};

export const isBrickLedgeFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.HOUSE_BRICKLEDGE_PORCH]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_DECK]: true,
  [FoundationCondition.HOUSE_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.BASEMENT_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_PORCH]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_DECK]: true,
  [FoundationCondition.GARAGE_BRICKLEDGE_EXTERIOR]: true,
  [FoundationCondition.PORCH_BRICKLEDGE_DECK]: true,
  [FoundationCondition.PORCH_BRICKLEDGE_EXTERIOR]: true,
};

export const isDisabledFoundationCondition: {
  [k in FoundationCondition]?: boolean;
} = {
  [FoundationCondition.HOUSE_DISABLED]: true,
  [FoundationCondition.BASEMENT_DISABLED]: true,
  [FoundationCondition.GARAGE_DISABLED]: true,
  [FoundationCondition.PORCH_DISABLED]: true,
  [FoundationCondition.DECK_DISABLED]: true,
};

export function foundationConditionPrettyName(
  foundationSettings: FoundationSystemSettings,
  foundationCondition: FoundationCondition,
) {
  let name = `Typical`;

  const houseCondition = foundationCondition;

  if (foundationSettings.foundationSubSystemType === FoundationSubSystemType.MONOLITHIC) {
    if (isSpanFoundationCondition[foundationCondition]) {
      name += ` Thickened Slab`;
    } else {
      name += ` Turndown`;
    }
  } else if (foundationSettings.foundationSubSystemType === FoundationSubSystemType.STEM_WALL_CIP) {
    name += ` ${StemWallType.CIP} Stem Wall`;
  } else {
    name += ` ${StemWallType.CMU} Stem Wall`;
  }

  const classes = houseCondition.split("_");
  const primaryRoomClass = classes[0];
  if (foundationCondition === FoundationCondition.GARAGE_DOOR_EXTERIOR) {
    name += ` @ Ext Garage Opening`;
  } else if (isSpanFoundationCondition[foundationCondition]) {
    name += ` @ ${primaryRoomClass} Load Bearing Wall`;
  } else if (isTransitionFoundationCondition[foundationCondition]) {
    name += ` Btwn ${snakeCaseToTitleCase(houseCondition.replace("_BRICKLEDGE", "").replace("_", "_&_"))}`;
  } else if (isPerimeterFoundationCondition[foundationCondition]) {
    if (isPorchFoundationCondition[foundationCondition]) {
      name += ` @ ${primaryRoomClass} Slab Edge`;
    } else if (isDeckFoundationCondition[foundationCondition]) {
      name += ` @ ${primaryRoomClass} Perimeter`;
    } else if (isDisabledFoundationCondition[foundationCondition]) {
      name += ` @ Disabled`;
    } else {
      name += ` @ Ext ${primaryRoomClass} Wall`;
    }
  }

  if (isBrickLedgeFoundationCondition[foundationCondition]) {
    name += ` /w Brick`;
  }

  return name;
}
